/* eslint-disable max-len */
// #region Sttedra
import {useDev} from '@sttedra_ui/framework';
// #endregion

// #region Vue
import {computed} from 'vue';
// #endregion

// #region Composables
import store from '@/pages/_root/store';
// #endregion

// #region Enums
// ...
// #endregion

const AGE_RANGES = {
  ANNUAL_PASS_TUTORIZE_STEP1: {min: 120, max: 18},
  ANNUAL_PASS_TUTORIZE_STEP2: {min: 14, max: 0},
  ANNUAL_PASS_NO_TUTORIZE: {min: 26, max: 14},
  YOUNG_SUMMER: {min: 30, max: 18},
};

/**
 * Resta una cantidad de años a una fecha dada, considerando día y mes.
 *
 * @param {Date} date - La fecha original.
 * @param {number} years - La cantidad de años a restar.
 * @returns {Date} La nueva fecha con los años restados.
 */
function subtractYears(date, years) {
  const newDate = new Date(date);
  newDate.setFullYear(newDate.getFullYear() - years);

  // Manejar años bisiestos y problemas de fin de mes
  if (newDate.getMonth() !== date.getMonth()) {
    newDate.setDate(0); // Establecer al último día del mes anterior
  }

  return newDate;
}

/**
 * Crea un objeto Date que representa el inicio de un año dado.
 *
 * @param {number} year - El año para el cual crear la fecha de inicio.
 * @returns {Date} El objeto Date creado establecido al 1 de enero del año especificado.
 */
function createStartOfYear(year) {
  return new Date(year, 0, 1); // 1 de enero
}

/**
 * Crea un objeto Date que representa el fin de un año dado.
 *
 * @param {number} year - El año para el cual crear la fecha de fin.
 * @returns {Date} El objeto Date creado establecido al 31 de diciembre del año especificado.
 */
function createEndOfYear(year) {
  return new Date(year, 11, 31); // 31 de diciembre
}

// /**
//  * Crea una fecha basada en el año proporcionado, manteniendo el mes y día actuales.
//  *
//  * @param {string} yearString - El año de referencia en formato string, por ejemplo, "2025".
//  * @returns {Date} La fecha creada con el año proporcionado y el mes y día actuales.
//  */
// function createDateWithYear(yearString) {
//   const parsedYear = parseInt(yearString, 10);
//   if (isNaN(parsedYear)) {
//     useDev.warn('[use-date-range] yearString proporcionado no es un año válido. Se usará la fecha actual.');
//     return new Date();
//   }

//   const today = new Date();
//   return new Date(parsedYear, today.getMonth(), today.getDate());
// }

/**
 * Obtiene las fechas de inicio y fin basándose en el paso proporcionado y el estado de la tienda.
 *
 * @param {number} [step=1] - El paso actual en el proceso.
 * @param {string} [yearString=null] - (Opcional) El año de referencia en formato string, por ejemplo, "2025".
 * @returns {{ minDate: Date | null, maxDate: Date | null , textError: String}} Un objeto que contiene las fechas mínima, máxima y error.
 */
function getStartAndEndDates(step = 1) {
  const isYoungSummer = computed(() => store.getters['app/isYoungSummer']);
  const isAnnualPass = computed(() => store.getters['app/isAnnualPass']);
  const isTutorizeMode = computed(() => store.getters['register/isTutorizeMode']);
  const productYear = computed(() => store.getters['app/getProductYear']);

  // Determinar la fecha de referencia al inicio del día de hoy
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const currentYear = productYear.value;

  let minDate = null;
  let maxDate = null;
  let textError = '';
  if (isAnnualPass.value) {
    if (isTutorizeMode.value) {
      if (step === 1) {
        // Verificar que la persona haya cumplido ya 14 años considerando día y mes
        const {min, max} = AGE_RANGES.ANNUAL_PASS_TUTORIZE_STEP1;
        minDate = createStartOfYear(currentYear - min); // 120 años atrás desde el inicio del año
        maxDate = subtractYears(today, max); // 18 años atrás desde 'today', considerando día y mes
        textError = `Fecha de nacimiento fuera del rango. Solo podrán realizar el registro del menor los tutores mayores a ${max} años.`;
      } else if (step === 2) {
        // De 0 a 13 años (considerando día y mes)
        const {min, max} = AGE_RANGES.ANNUAL_PASS_TUTORIZE_STEP2;
        const baseMin = subtractYears(today, min);
        minDate = new Date(baseMin);
        minDate.setDate(minDate.getDate() + 1); // excluye a quien cumple 14 hoy
        maxDate = subtractYears(today, max); // hace 14 años desde hoy (incluye quienes acaban de nacer hasta casi 14)
        textError = `Este formulario está destinado exclusivamente a personas menores de 14 años. En caso de que el menor haya cumplido ya los 14 años, por favor utilice la alternativa correspondiente para jóvenes entre 14 y 26 años.`;
      }
    } else {
      // De 14 a 26 años (solo por año)
      const {min, max} = AGE_RANGES.ANNUAL_PASS_NO_TUTORIZE;
      minDate = createStartOfYear(currentYear - min); // 26 años atrás desde el inicio del año
      maxDate = subtractYears(today, max); // 14 años atrás desde 'today', considerando día y mes
      textError = `Este formulario está destinado exclusivamente a personas de 14 a 26 años. En caso de que el menor no haya cumplido ya los 14 años, por favor utilice la alternativa correspondiente para jóvenes entre 0 y 13 años.`;
    }
  } else if (isYoungSummer.value) {
    // De 18 a 30 años (solo por año)
    const {min, max} = AGE_RANGES.YOUNG_SUMMER;
    minDate = createStartOfYear(currentYear - min); // 30 años atrás desde el inicio del año
    maxDate = createEndOfYear(currentYear - max); // 18 años atrás hasta el fin del año
    textError = `Fecha de nacimiento fuera del rango. Solo podrán beneficiarse de esta subvención los jóvenes nacidos entre el 1 de enero de ${minDate.getFullYear()} y el 31 de diciembre de ${maxDate.getFullYear()}.`;
  } else {
    useDev.warn('[use-date-range] No age range condition matched.');
  }

  if (maxDate && maxDate > today) {
    maxDate = today;
    // Ajustar texto de error con la nueva fecha máxima real
    textError = `Fecha de nacimiento fuera del rango. Solo podrán beneficiarse de esta subvención los jóvenes nacidos entre el 1 de enero de ${minDate.getFullYear()} y el ${today.toLocaleDateString('es-ES', {day: '2-digit', month: 'long', year: 'numeric'})}.`;
  }

  return {minDate, maxDate, textError};
}

const result = Object.freeze({
  getStartAndEndDates,
});

/**
 * Provee funcionalidades para rangos de fechas.
 *
 * @returns {Object} Un objeto que contiene la función `getStartAndEndDates`.
 */
export default function() {
  return result;
}
